import classNames from 'classnames';
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useIsMounted } from '../hooks/useIsMounted';

import './Tooltip.css';

/*
 * For how to use guide see Tooltip.md
 */

export type TooltipProps = {
    children?: React.ReactNode;
    className?: string;
    tooltipClassName?: string;
    tooltipText?: string | React.ReactNode;
    fadeInDuration?: TooltipFadeInDuration;
    delay?: number;
    direction?: TooltipDirection;
    usePortal?: boolean; // New prop to enable portal rendering
    isDisabled?: boolean;
};

export enum TooltipDirection {
    Top = 'top',
    Right = 'right',
    Bottom = 'bottom',
    Left = 'left',
    TopLeft = 'top-left',
    TopRight = 'top-right',
    BottomLeft = 'bottom-left',
    BottomRight = 'bottom-right'
}

export enum TooltipFadeInDuration {
    _75 = 'duration-75',
    _100 = 'duration-100',
    _150 = 'duration-150',
    _200 = 'duration-200',
    _300 = 'duration-300',
    _500 = 'duration-500',
    _700 = 'duration-700',
    _1000 = 'duration-1000'
}

function Tooltip(props: TooltipProps): React.JSX.Element {
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const hiddenTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [active, setActive] = useState(false);
    const [hidden, setHidden] = useState(true);
    const [position, setPosition] = useState<{
        top: number;
        left: number;
        arrowLeft: number;
        placement: 'top' | 'bottom';
    }>({ top: 0, left: 0, arrowLeft: 0, placement: 'top' });
    // Only render portal on client to avoid hydration mismatch
    const isMounted = useIsMounted();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const showTip = () => {
        // Touch-only devices: a tap fires a synthetic mouseenter but never a mouseleave, so the
        // tooltip would appear and then stick around (even on top of the modal the tap opened).
        // Hover affordances are pointless without a hover-capable pointer — skip entirely.
        if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) {
            return;
        }
        setHidden(false);
        clearTimeout(hiddenTimeoutRef.current);
        showTimeoutRef.current = setTimeout(() => {
            setActive(true);
            if (props.usePortal && wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                const direction = props.direction || TooltipDirection.Top;
                const gap = 8;
                const viewportMargin = 8;

                // The tooltip is already rendered (just transparent), so its real size can be
                // measured and used for viewport-aware placement/clamping.
                const tooltipWidth = tooltipRef.current?.offsetWidth ?? 0;
                const tooltipHeight = tooltipRef.current?.offsetHeight ?? 0;

                // Vertical: prefer above the trigger, but flip below when the tooltip would poke
                // out of the top viewport edge (e.g. triggers in the nav bar on scrolled pages).
                // The flipped position is additionally clamped to the bottom edge as a last resort.
                const fitsAbove = rect.top - gap - tooltipHeight >= viewportMargin;
                const placement: 'top' | 'bottom' = fitsAbove ? 'top' : 'bottom';
                const top = fitsAbove
                    ? rect.top - gap
                    : Math.min(
                          rect.bottom + gap,
                          Math.max(viewportMargin, window.innerHeight - viewportMargin - tooltipHeight)
                      );
                // X is computed in pixels so it can be clamped to the viewport — a centred/
                // right-aligned tooltip near the edge would otherwise stick out of narrow windows.
                let left: number;
                switch (direction) {
                    case TooltipDirection.TopRight:
                        left = rect.right - tooltipWidth; // right-aligned with the trigger
                        break;
                    case TooltipDirection.TopLeft:
                        left = rect.left; // left-aligned with the trigger
                        break;
                    case TooltipDirection.Top:
                    default:
                        left = rect.left + rect.width / 2 - tooltipWidth / 2; // centred on the trigger
                }
                left = Math.min(
                    Math.max(left, viewportMargin),
                    Math.max(viewportMargin, window.innerWidth - viewportMargin - tooltipWidth)
                );

                // Keep the arrow anchored to the trigger's centre even when the tooltip body was
                // shifted by the viewport clamping (kept a safe distance from the rounded corners).
                const arrowInset = 10;
                const arrowLeft = Math.min(
                    Math.max(rect.left + rect.width / 2 - left, arrowInset),
                    Math.max(arrowInset, tooltipWidth - arrowInset)
                );

                setPosition({ top, left, arrowLeft, placement });
            }
        }, props.delay || 50);
    };

    const hideTip = () => {
        clearTimeout(showTimeoutRef.current);
        setActive(false);
        hiddenTimeoutRef.current = setTimeout(() => {
            setHidden(true);
        }, props.delay || 50);
    };

    // When the portal placement flipped below the trigger, swap the direction class so the CSS
    // arrow renders on the tooltip's top edge, pointing up at the trigger.
    const flippedBelow = props.usePortal && position.placement === 'bottom';

    const tooltipElement = (
        <div
            ref={props.usePortal ? tooltipRef : undefined}
            className={classNames(
                `Tooltip-Tip transition-opacity ${active ? 'opacity-100' : 'opacity-0'} ${hidden ? 'hidden' : ''}`,
                flippedBelow ? TooltipDirection.Bottom : props.direction || TooltipDirection.Top,
                props.fadeInDuration || TooltipFadeInDuration._150,
                props.tooltipClassName
            )}
            style={
                props.usePortal
                    ? {
                          position: 'fixed',
                          top: position.top,
                          // .Tooltip-Tip.bottom positions inline tips via `bottom` — neutralise it,
                          // the portal is fully positioned through `top`.
                          bottom: 'auto',
                          left: position.left,
                          zIndex: 9999,
                          // X alignment/clamping is baked into `left` (see showTip) — vertically the
                          // tooltip is lifted above the trigger unless it flipped below it.
                          transform: flippedBelow ? 'none' : 'translateY(-100%)',
                          // Consumed by .Tooltip-Tip::before so the arrow keeps pointing at the
                          // trigger even when the body was clamped to the viewport.
                          ['--tooltip-arrow-left' as string]: `${position.arrowLeft}px`
                      }
                    : undefined
            }>
            {props.tooltipText}
        </div>
    );

    if (props.isDisabled) {
        return props.children as React.ReactElement;
    }

    return (
        <div
            ref={wrapperRef}
            // z-modal-view only for inline (non-portal) tips, which live inside this wrapper and need
            // it to float above neighbours. When portaled, the tip renders at document.body with its
            // own zIndex, so lifting the wrapper (and thus the trigger) to 9000 just makes the trigger
            // cover unrelated overlays like the nav profile dropdown.
            className={classNames(props.className, 'Tooltip-Wrapper', !props.usePortal && 'z-modal-view')}>
            {/* onClick hides the tip: clicking the trigger usually opens an overlay (modal/menu),
            and a lingering tooltip would sit on top of it (portal tips render at z 9999). */}
            <div
                onMouseEnter={showTip}
                onMouseLeave={hideTip}
                onClick={hideTip}>
                {props.children}
            </div>
            {props.usePortal ? (isMounted ? createPortal(tooltipElement, document.body) : null) : tooltipElement}
        </div>
    );
}

export default Tooltip;
